import { useQuery } from '@tanstack/react-query';
import { AtSign, Check, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSetUsername } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api/client';
import { me, users } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

/**
 * Choosing a handle — the last step of registration.
 *
 * The backend suggests one derived from the Google profile; the user may keep
 * it or change it. A handle is how other people delegate work to you, so it is
 * worth one screen rather than being assigned silently.
 */
export function OnboardingRoute() {
  const t = useT();
  // `null` means "the user has not typed yet", so the server's suggestion is
  // shown. Deriving it this way avoids an effect that copies a fetched value
  // into state and re-renders once more for every load.
  const [typed, setTyped] = useState<string | null>(null);
  const setUsernameMutation = useSetUsername();

  const { data: suggestion } = useQuery({
    queryKey: ['username-suggestion'],
    queryFn: () => me.usernameSuggestion(),
    staleTime: Infinity,
  });

  const username = typed ?? suggestion?.suggested ?? '';
  const candidate = username.trim().replace(/^@/, '').toLowerCase();

  const { data: availability, isFetching: checking } = useQuery({
    queryKey: ['username-available', candidate],
    queryFn: () => users.usernameAvailable(candidate),
    enabled: candidate.length >= 2,
    staleTime: 5_000,
  });

  const available = availability?.available ?? false;
  const canSubmit = available && !setUsernameMutation.isPending;

  const serverError =
    setUsernameMutation.error instanceof ApiError
      ? setUsernameMutation.error.message
      : null;

  return (
    <div className="flex h-full items-center justify-center px-6">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) setUsernameMutation.mutate(candidate);
        }}
        className="w-full max-w-sm"
      >
        <h1 className="mb-1.5 text-2xl font-semibold tracking-tight">
          {t('onboarding.title')}
        </h1>
        <p className="mb-6 text-sm text-content-muted">
          {t('onboarding.hint')}{' '}
          <span className="text-content">@{candidate || t('onboarding.example')}</span>
        </p>

        <div className="relative mb-1.5">
          <AtSign
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-content-subtle"
            aria-hidden
          />
          <Input
            autoFocus
            value={username}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={t('onboarding.example')}
            aria-label={t('onboarding.username')}
            aria-describedby="username-status"
            className="pl-8 pr-9"
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {checking ? (
              <Loader2
                className="h-4 w-4 animate-spin text-content-subtle"
                aria-hidden
              />
            ) : available ? (
              <Check className="h-4 w-4 text-success" aria-hidden />
            ) : null}
          </span>
        </div>

        <p
          id="username-status"
          className={cn(
            'mb-5 min-h-[1.25rem] text-xs',
            available ? 'text-success' : 'text-content-subtle',
          )}
          role="status"
        >
          {candidate.length < 2
            ? t('onboarding.rules')
            : (availability?.reason ?? (available ? t('onboarding.available') : ''))}
        </p>

        {suggestion?.alternatives.length ? (
          <div className="mb-5">
            <p className="mb-1.5 text-xs text-content-subtle">{t('onboarding.or')}</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestion.alternatives.map((alternative) => (
                <button
                  key={alternative}
                  type="button"
                  onClick={() => setTyped(alternative)}
                  className="rounded border border-border px-2 py-1 text-xs text-content-muted transition-surface hover:bg-surface-hover"
                >
                  @{alternative}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {serverError ? (
          <p className="mb-3 text-sm text-danger" role="alert">
            {serverError}
          </p>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!canSubmit}
        >
          {setUsernameMutation.isPending ? t('common.saving') : t('common.continue')}
        </Button>

        <p className="mt-3 text-xs text-content-subtle">{t('onboarding.changeable')}</p>
      </form>
    </div>
  );
}
